import { useMutation, useQuery, useQueryClient } from 'react-query';
import { positionOpenerAxiosApi } from 'src/Config/axiosApi';
import {
  applyBulkArmedDtos,
  applyBulkMakerFlagDtos,
  asBulkGesture,
  buildBulkArmedDtos,
  buildBulkMakerFlagDtos
} from '../components/entry/bulkScope';
import {
  aggregatePrepareResults,
  resolveTurnOnRuleIds,
  type ExistingRuleMakerState,
  type MatchPrepareRequest
} from '../components/entry/soccerMatchSelection';
import { TAB_FLAGS } from '../components/entry/entryMath';
import { debouncedInvalidateEntryRules, useBulkSetArmed, useBulkSetMakerFlag } from './useEntry';

/**
 * The Soccer Match Drawer's read model + prepare mutation — CORE-pod-only
 * routes on `position-opener` (`positionOpenerAxiosApi`, NEVER the per-group
 * feed base). Backend contract per the shipped `position-opener` unit (spec
 * `arbitrage-core/docs/specs/2026-08-04-opener-soccer-match-drawer.md`,
 * section C, as corrected by the implementation plan).
 */

export interface SoccerMatchBucketConfigEcho {
  subgroup: string;
  thresholdSum: number;
  size: number;
}

export type SoccerOpenerBucket =
  | 'moneyline'
  | 'spread'
  | 'total'
  | 'btts_ftts'
  | 'exact_score'
  | 'first_half_result'
  | 'first_half_total'
  | 'player_props'
  | 'corners'
  | 'other';

export interface SoccerMatchBucketState {
  assigned: number;
  enabled: number;
  ruled: number;
  armed: number;
}

export interface SoccerMatchBucketRow {
  bucket: SoccerOpenerBucket;
  pairCount: number;
  configSubgroups: string[];
  /** ALWAYS true in this implementation — every returned bucket is
   *  immediately actionable, there is no "disabled checkbox" case. */
  configResolved: true;
  config: SoccerMatchBucketConfigEcho[];
  state: SoccerMatchBucketState;
  /** Owner-decided capital-risk override (2026-08-06): Exact 0-0, Exact
   *  other zero, and FTTS "neither team scores first" pairs arm NO only,
   *  never YES, regardless of the active Path tab — see
   *  `soccerOpenerArmDirectiveFor` in `@arbitrage-core/matching-core`. A
   *  bucket can mix both kinds of pairs (e.g. exact_score has some 0-0/
   *  other-zero pairs and some no-zero pairs), so this is a per-bucket
   *  tally, not a single flag. */
  armDirectiveCounts: { path: number; noOnly: number };
}

/**
 * What the backend's market-semantics deriver made of this match's pairs.
 *
 * Match-level ONLY — the drawer has no per-pair row at any depth, and adding
 * the first one was scoped out. `byShape` is what replaces the human eye a
 * per-pair list would have given: a single flipped `yesMeaning` is invisible in
 * aggregate, but a distribution that is obviously wrong (0 moneyline in a
 * 46-pair match, or 40 unresolved) is legible at a glance. Until the detector
 * that consumes these semantics exists, this is the operator's only window onto
 * the derivation.
 *
 * `outOfScope` is counted apart from `unresolved` deliberately: corners and
 * player props are markets the deriver RECOGNISES and does not describe, and
 * folding them into a failure count would read as six broken rows on every rich
 * slate.
 */
export interface SoccerMatchSemanticsSummary {
  /** `null` when the match's own markets did not name exactly two competitors. */
  roster: Array<{ code: string; label: string }> | null;
  resolved: number;
  outOfScope: number;
  unresolved: number;
  /** Unresolved + out-of-scope reasons, by reason. Raw backend vocabulary. */
  reasons: Record<string, number>;
  /** Resolved pairs by market shape. Raw backend vocabulary. */
  byShape: Record<string, number>;
}

export interface SoccerMatchRow {
  matchKey: string;
  family: string;
  gameSuffix: string;
  /** Human match name ("Club Brugge vs. KV Kortrijk"), resolved backend-side
   *  from the pairs' event titles. `null` when no pair carries one — the row
   *  then falls back to `family`/`gameSuffix`. */
  matchName: string | null;
  /** Kick-off, from Polymarket's `startDate` (Kalshi's is the market's
   *  trading-open time, days earlier — see the backend's resolver). */
  kickoff: string | null;
  /** Deep links to the match's main event page on each platform. */
  polyEventUrl: string | null;
  kalshiEventUrl: string | null;
  pairCount: number;
  /** Only NON-EMPTY buckets, already in the canonical bucket order. */
  buckets: SoccerMatchBucketRow[];
  /**
   * Optional so a browser running this bundle against a pod that predates the
   * field renders the match without it, rather than throwing. Every consumer
   * below must therefore guard.
   */
  semantics?: SoccerMatchSemanticsSummary;
}

export interface SoccerMatchesReadModel {
  groupId: string;
  matches: SoccerMatchRow[];
  unclassifiedPairCount: number;
  unclassified: { pairCount: number; reasons: Record<string, number> };
}

export interface PrepareSoccerMatchesDto {
  groupId: string;
  matchKeys: string[];
  buckets: string[];
  path?: 'a' | 'b';
  dryRun: boolean;
}

export interface PrepareSoccerMatchesSkip {
  pairId: string;
  bucket: string;
  reason: string;
}

export interface PrepareSoccerMatchesResult {
  dryRun: boolean;
  subgroups: { created: number; updated: number };
  pairs: {
    matched: number;
    assigned: number;
    alreadyAssigned: number;
    enabled: number;
  };
  skipped: PrepareSoccerMatchesSkip[];
  rules: { created: number; skipped: number };
  /** pairId -> its entry-rule id (existing OR just-created), for every
   *  successfully-prepared pair. */
  ruleIdsByPairId: Record<string, string>;
  /** pairId -> `soccerOpenerArmDirectiveFor`'s decision, for every pair in
   *  `ruleIdsByPairId`. Computed ONCE, authoritatively, by the backend —
   *  this FE only reads it to decide which bulk-maker-flag calls to issue,
   *  it never re-derives the rule (matching-core isn't available here, and
   *  a capital-risk arming decision must not have two implementations that
   *  can drift). */
  armDirectiveByPairId: Record<string, 'path' | 'no-only'>;
}

/** Returned by `useTurnOnSoccerMatches`/`useTurnOffSoccerMatches` — the
 *  aggregated prepare result PLUS the pairs `resolveTurnOnRuleIds` could
 *  not resolve to any direction (genuinely unknown, e.g. a corrupted maker-
 *  flag state) and therefore left completely untouched. The caller MUST
 *  surface a non-empty `unresolvedPairIds` to the operator — the confirm
 *  dialog otherwise claims "ARMS every resulting rule" without qualification. */
export interface TurnOnOffResult extends PrepareSoccerMatchesResult {
  unresolvedPairIds: string[];
}

/**
 * Read model for the drawer. `open` gates BOTH `enabled` and the 15s
 * `refetchInterval` (spec §4.7) — pass `true` from a caller that wants a
 * standing subscription regardless of the drawer's own open state (the
 * "Matches" button's visibility gate on `EntryPage` is one such caller: it
 * needs to know `matches.length` before the drawer ever opens).
 */
export function useSoccerMatches(groupId: string | null, open: boolean) {
  return useQuery<SoccerMatchesReadModel>(
    ['soccer-matches', groupId],
    async () => {
      const { data } = await positionOpenerAxiosApi.get<SoccerMatchesReadModel>(
        '/rules/soccer-matches',
        { params: { groupId } }
      );
      return data;
    },
    {
      enabled: open && !!groupId,
      refetchInterval: open ? 15000 : false,
      keepPreviousData: true
    }
  );
}

export function usePrepareSoccerMatches() {
  return useMutation(
    async (dto: PrepareSoccerMatchesDto) => {
      const { data } = await positionOpenerAxiosApi.post<PrepareSoccerMatchesResult>(
        '/rules/soccer-matches/prepare',
        dto
      );
      return data;
    },
    { mutationKey: 'entry:soccer-matches-prepare' }
  );
}

/**
 * The single "Turn on match" gesture (plan D4): one operator click, several
 * network calls underneath — one real (`dryRun:false`) prepare call PER
 * SELECTED MATCH (see `soccerMatchSelection.ts` for why not one flat call),
 * then the existing bulk maker-flag (poly + kalshi) and bulk-armed applies,
 * reused verbatim from `bulkScope.ts`, split across two rule-id groups by
 * `armDirectiveByPairId` (owner-decided capital-risk rule, 2026-08-06):
 *   - `'path'` (the default): arm `TAB_FLAGS[activePath]`, as before.
 *   - `'no-only'`: Exact 0-0 / Exact other zero / FTTS "neither scores
 *     first" — arm BOTH `polyNoMaker`+`kalshiNoMaker` and explicitly
 *     disarm BOTH YES flags, ignoring the active Path entirely.
 * Wrapped in one `useMutation` so ANY step failing surfaces via the app-wide
 * `MutationCache.onError` backstop under `entry:soccer-matches-turn-on`.
 */
export function useTurnOnSoccerMatches() {
  const qc = useQueryClient();
  const prepare = usePrepareSoccerMatches();
  const bulkMakerFlag = useBulkSetMakerFlag();
  const bulkArmed = useBulkSetArmed();
  return useMutation(
    async (vars: {
      groupId: string;
      path: 'a' | 'b';
      requests: MatchPrepareRequest[];
      rulesByPairId: ReadonlyMap<string, ExistingRuleMakerState>;
    }) => {
      return asBulkGesture('turn on', async collect => {
        const results: PrepareSoccerMatchesResult[] = [];
        for (const req of vars.requests) {
          const result = await prepare.mutateAsync({
            groupId: vars.groupId,
            matchKeys: [req.matchKey],
            buckets: req.buckets,
            path: vars.path,
            dryRun: false
          });
          results.push(result);
        }
        const aggregate = aggregatePrepareResults(results);
        const { pathRuleIds, noOnlyRuleIds, unresolvedPairIds } = resolveTurnOnRuleIds(
          aggregate,
          vars.rulesByPairId
        );
        if (pathRuleIds.length > 0) {
          const pathSelection = { kind: 'selected' as const, ruleIds: pathRuleIds };
          const otherPath = vars.path === 'a' ? 'b' : 'a';
          // Explicitly clear the OTHER path's two flags, not just set this
          // path's two — a rule id resolved via the "already has a rule"
          // fallback (see `resolveTurnOnRuleIds`) may have been armed under a
          // DIFFERENT path (or no-only) in a previous run; without this, a
          // Path A re-arm on a rule last armed under Path B would leave all
          // four maker flags true simultaneously, breaking the schema's
          // path-exclusivity contract (`entry.types.ts`'s `EntryRule` comment).
          await applyBulkMakerFlagDtos(
            buildBulkMakerFlagDtos(pathSelection, TAB_FLAGS[otherPath].poly, false, []),
            dto => bulkMakerFlag.mutateAsync(dto),
            collect
          );
          await applyBulkMakerFlagDtos(
            buildBulkMakerFlagDtos(pathSelection, TAB_FLAGS[otherPath].kalshi, false, []),
            dto => bulkMakerFlag.mutateAsync(dto),
            collect
          );
          await applyBulkMakerFlagDtos(
            buildBulkMakerFlagDtos(pathSelection, TAB_FLAGS[vars.path].poly, true, []),
            dto => bulkMakerFlag.mutateAsync(dto),
            collect
          );
          await applyBulkMakerFlagDtos(
            buildBulkMakerFlagDtos(pathSelection, TAB_FLAGS[vars.path].kalshi, true, []),
            dto => bulkMakerFlag.mutateAsync(dto),
            collect
          );
          await applyBulkArmedDtos(
            buildBulkArmedDtos(pathSelection, true, []),
            dto => bulkArmed.mutateAsync(dto),
            collect
          );
        }
        if (noOnlyRuleIds.length > 0) {
          const noOnlySelection = { kind: 'selected' as const, ruleIds: noOnlyRuleIds };
          // Never arm YES for these — explicit, not just "leave unset", in case
          // a prior session armed YES on one of these pairs under a different
          // rule (e.g. before this override existed).
          await applyBulkMakerFlagDtos(
            buildBulkMakerFlagDtos(noOnlySelection, 'polyYesMaker', false, []),
            dto => bulkMakerFlag.mutateAsync(dto),
            collect
          );
          await applyBulkMakerFlagDtos(
            buildBulkMakerFlagDtos(noOnlySelection, 'kalshiYesMaker', false, []),
            dto => bulkMakerFlag.mutateAsync(dto),
            collect
          );
          await applyBulkMakerFlagDtos(
            buildBulkMakerFlagDtos(noOnlySelection, 'polyNoMaker', true, []),
            dto => bulkMakerFlag.mutateAsync(dto),
            collect
          );
          await applyBulkMakerFlagDtos(
            buildBulkMakerFlagDtos(noOnlySelection, 'kalshiNoMaker', true, []),
            dto => bulkMakerFlag.mutateAsync(dto),
            collect
          );
          await applyBulkArmedDtos(
            buildBulkArmedDtos(noOnlySelection, true, []),
            dto => bulkArmed.mutateAsync(dto),
            collect
          );
        }
        return { ...aggregate, unresolvedPairIds };
      });
    },
    {
      mutationKey: 'entry:soccer-matches-turn-on',
      onSettled: () => {
        debouncedInvalidateEntryRules(qc);
        qc.invalidateQueries('soccer-matches');
      }
    }
  );
}

/**
 * "Turn off match" (plan D5) — disarm only, reversible: `openerEnabled` and
 * the rules themselves are left alone. Deliberately resolves its rule ids via
 * a `dryRun:true` prepare call (zero writes, guaranteed) rather than reusing
 * the real write path — Turn Off must NEVER assign a subgroup / flip
 * `openerEnabled` as a side effect of merely looking up which rules exist.
 */
export function useTurnOffSoccerMatches() {
  const qc = useQueryClient();
  const prepare = usePrepareSoccerMatches();
  const bulkMakerFlag = useBulkSetMakerFlag();
  const bulkArmed = useBulkSetArmed();
  return useMutation(
    async (vars: {
      groupId: string;
      path: 'a' | 'b';
      requests: MatchPrepareRequest[];
      rulesByPairId: ReadonlyMap<string, ExistingRuleMakerState>;
    }) => {
      return asBulkGesture('turn off', async collect => {
        const results: PrepareSoccerMatchesResult[] = [];
        for (const req of vars.requests) {
          const result = await prepare.mutateAsync({
            groupId: vars.groupId,
            matchKeys: [req.matchKey],
            buckets: req.buckets,
            path: vars.path,
            dryRun: true
          });
          results.push(result);
        }
        const aggregate = aggregatePrepareResults(results);
        // Turn Off doesn't care which side was armed (arm direction only
        // matters when turning ON) — flatten both groups and disarm together.
        const { pathRuleIds, noOnlyRuleIds, unresolvedPairIds } = resolveTurnOnRuleIds(
          aggregate,
          vars.rulesByPairId
        );
        // `unresolvedPairIds` fails a pair closed when its maker flags don't
        // match any known direction signature — correct for Turn On (never
        // guess which side to arm), but Turn Off doesn't need a direction at
        // all, only the rule id. A pair in this bucket whose rule IS still
        // known (an over-set/inconsistent flag state, not a missing rule) must
        // still be disarmed — dropping it here would leave a rule `armed:true`
        // with live maker quotes, silently un-disarmable by this action ever
        // again. Only a pair with NO known rule at all stays genuinely
        // unresolved (nothing to disarm).
        const unresolvedRuleIds: string[] = [];
        const trulyUnresolvedPairIds: string[] = [];
        for (const pairId of unresolvedPairIds) {
          const ruleId = vars.rulesByPairId.get(pairId)?._id;
          if (ruleId) {
            unresolvedRuleIds.push(ruleId);
          } else {
            trulyUnresolvedPairIds.push(pairId);
          }
        }
        const ruleIds = [
          ...new Set([...pathRuleIds, ...noOnlyRuleIds, ...unresolvedRuleIds])
        ];
        if (ruleIds.length > 0) {
          const selection = { kind: 'selected' as const, ruleIds };
          // Clear ALL FOUR maker flags, not just `TAB_FLAGS[activePath]`'s two —
          // a no-only rule has its two NO flags true regardless of the active
          // Path, and clearing only the active path's two would leave one NO
          // flag dangling `true`. That leftover flag then corrupts
          // `resolveTurnOnRuleIds`'s "both NO true" no-only re-detection on the
          // NEXT Turn On (the pair's rule persists across Off/On — prepare only
          // ever creates once): the pair falls through to `pathRuleIds` on the
          // next run and gets `polyYesMaker`/`kalshiYesMaker` armed on top of
          // the surviving NO flag — the exact live-capital-risk bug this
          // feature exists to prevent, reachable through an ordinary
          // Off-then-On cycle. `desiredArmed:false` alone already stops the
          // engine from trading; this additionally returns the rule to a
          // clean, unambiguous baseline for whichever direction arms it next.
          await applyBulkMakerFlagDtos(
            buildBulkMakerFlagDtos(selection, 'polyYesMaker', false, []),
            dto => bulkMakerFlag.mutateAsync(dto),
            collect
          );
          await applyBulkMakerFlagDtos(
            buildBulkMakerFlagDtos(selection, 'polyNoMaker', false, []),
            dto => bulkMakerFlag.mutateAsync(dto),
            collect
          );
          await applyBulkMakerFlagDtos(
            buildBulkMakerFlagDtos(selection, 'kalshiYesMaker', false, []),
            dto => bulkMakerFlag.mutateAsync(dto),
            collect
          );
          await applyBulkMakerFlagDtos(
            buildBulkMakerFlagDtos(selection, 'kalshiNoMaker', false, []),
            dto => bulkMakerFlag.mutateAsync(dto),
            collect
          );
          await applyBulkArmedDtos(
            buildBulkArmedDtos(selection, false, []),
            dto => bulkArmed.mutateAsync(dto),
            collect
          );
        }
        return { ...aggregate, unresolvedPairIds: trulyUnresolvedPairIds };
      });
    },
    {
      mutationKey: 'entry:soccer-matches-turn-off',
      onSettled: () => {
        debouncedInvalidateEntryRules(qc);
        qc.invalidateQueries('soccer-matches');
      }
    }
  );
}
